
import { Icon } from 'semantic-ui-react';
import styles from './PersonListItem.module.css';

type Person = {
    id: number;
    name: string;
    identifier: string;
    photoUrl: string;
    preferredName?: string | null;
    scriptName?: string | null;
    tags?: { name: string }[];
    /** Null outside a supporter organisation, or where the viewer may not see this person. */
    lastSeenAt?: string | null;
};

/**
 * "2 days ago" rather than a date, because the question being asked is **how long**, not when.
 * An administrator scanning a congregation is looking for people who have gone quiet, and
 * "14 Sept" makes them do the subtraction on every row.
 */
const lastSeen = (iso: string | null | undefined): string | null => {
    if (!iso) return null;

    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;

    const days = Math.floor((Date.now() - then) / 86400000);

    if (days <= 0) return 'Active today';
    if (days === 1) return 'Active yesterday';
    if (days < 14) return `Active ${days} days ago`;
    if (days < 60) return `Active ${Math.floor(days / 7)} weeks ago`;
    return `Active ${Math.floor(days / 30)} months ago`;
};

const PersonListItem = ({
    person,
    onClick,
    showTags = false,
}: {
    person: Person;
    onClick: () => void;
    showTags?: boolean;
}) => {
    const displayName = person.preferredName
        ? `${person.name} (${person.preferredName})`
        : person.name;

    const metaParts = [person.scriptName, `ID: ${person.identifier}`].filter(Boolean);
    const seen = lastSeen(person.lastSeenAt);

    // Only where the server supplied it. Everybody in a supporter organisation who has never
    // opened the app reads "Never signed in", which is the useful half of this field — but in a
    // community the field is absent entirely and nothing is said.
    const activity = person.lastSeenAt === undefined ? null : (seen ?? 'Never signed in');

    return (
        <div className={styles.item} onClick={onClick}>
            <img src={person.photoUrl} alt={person.name} className={styles.avatar} />
            <div className={styles.info}>
                <div className={styles.name}>{displayName}</div>
                <div className={styles.meta}>
                    {metaParts.join(' · ')}
                    {activity && (
                        <span className={seen ? styles.active : styles.dormant}>
                            {' · '}{activity}
                        </span>
                    )}
                </div>
                {showTags && person.tags && person.tags.length > 0 && (
                    <div className={styles.tags}>
                        {person.tags.map(t => (
                            <span key={t.name} className={styles.tag}>{t.name}</span>
                        ))}
                    </div>
                )}
            </div>
            <Icon name="chevron right" className={styles.chevron} />
        </div>
    );
};

export { PersonListItem };
export type { Person };
